import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import {
  PageHead, Card, Kpi, Pill, StatusPill, HealthDot, Btn, Loading, Err, tableStyles,
} from "./_shared";
import { Zap, RefreshCw, Sparkles, ServerCog, Activity } from "lucide-react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const d = await api.get("/api/admin/dashboard");
      setData(d);
      if (typeof window !== "undefined") window.__pl_env = d.environment;
    } catch (e) { setErr(e.message); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  if (err) return <Err msg={err} />;
  if (!data) return <Loading />;

  const k = data.kpis;
  const h = data.health;

  return (
    <div data-testid="admin-dashboard">
      <PageHead
        title="Mission Control"
        sub={`Environment · ${data.environment} · ${data.admin?.email}`}
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <Btn testId="dashboard-refresh" onClick={load} tone="ghost">
              <RefreshCw size={13} style={{ opacity: refreshing ? 0.5 : 1 }} /> Refresh
            </Btn>
            <Btn testId="dashboard-open-workspaces" onClick={() => { window.location.assign("/admin/workspaces"); }} tone="primary">
              <Sparkles size={13} /> Open workspaces
            </Btn>
          </div>
        }
      />

      {/* KPI grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))", gap: 12 }}>
        <Kpi testId="kpi-workspaces"        label="Workspaces"          value={k.total_workspaces}      sub={`${k.active_workspaces} active`} />
        <Kpi testId="kpi-users"             label="Users"               value={k.total_users} />
        <Kpi testId="kpi-jobs-today"        label="Jobs today"          value={k.jobs_today}            sub={`${k.succeeded_today} succeeded`} />
        <Kpi testId="kpi-failed-today"      label="Failed today"        value={k.failed_today}          tone="danger" />
        <Kpi testId="kpi-stuck-jobs"        label="Stuck jobs"          value={k.stuck_jobs}            tone="warn" sub="queued · claimed · running" />
        <Kpi testId="kpi-credits-used"      label="Credits used today"  value={k.credits_used_today} />
        <Kpi testId="kpi-credits-reserved"  label="Credits reserved"    value={k.credits_reserved_total} sub="across all workspaces" />
        <Kpi testId="kpi-support-sessions"  label="Support sessions"    value={k.active_support_sessions} sub="active" />
        <Kpi testId="kpi-providers-healthy" label="Providers healthy"   value={`${k.providers_healthy}/${k.providers_total || "—"}`} sub="model gateway" />
      </div>

      {/* Health row */}
      <div style={{ marginTop: 18 }}>
        <Card title="Platform health">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            {Object.entries(h).map(([key, val]) => (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>
                  {key.replace(/_/g, " ")}
                </div>
                <HealthDot status={val} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Quick actions + Quick Job */}
      <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 16 }}>
        <QuickJob workspaces={null} onCreated={load} />
        <Card title="Quick navigation">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <QuickLink to="/admin/workspaces"      label="All workspaces"   icon={<ServerCog size={14} />} />
            <QuickLink to="/admin/jobs"            label="AI job queue"      icon={<Activity size={14} />} />
            <QuickLink to="/admin/credits"         label="Credit ledger"     icon={<Activity size={14} />} />
            <QuickLink to="/admin/feature-flags"   label="Feature flags"     icon={<Activity size={14} />} />
            <QuickLink to="/admin/plans"           label="Plans & pricing"   icon={<Activity size={14} />} />
            <QuickLink to="/admin/model-providers" label="Model providers"   icon={<Activity size={14} />} />
            <QuickLink to="/admin/system-events"   label="System events"     icon={<Activity size={14} />} />
            <QuickLink to="/admin/audit-logs"      label="Audit logs"        icon={<Activity size={14} />} />
          </div>
        </Card>
      </div>

      {/* Recent feeds */}
      <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 16 }}>
        <Card title="Recent jobs" right={<Link to="/admin/jobs" style={linkStyle}>View all →</Link>}>
          <RecentJobs items={data.recent_jobs} />
        </Card>
        <Card title="Recent transactions" right={<Link to="/admin/credits" style={linkStyle}>View all →</Link>}>
          <RecentTransactions items={data.recent_transactions} />
        </Card>
        <Card title="System events" right={<Link to="/admin/system-events" style={linkStyle}>View all →</Link>}>
          <RecentEvents items={data.recent_events} />
        </Card>
      </div>
    </div>
  );
}

function QuickLink({ to, label, icon }) {
  return (
    <Link to={to} data-testid={`dash-quick-${to.split("/").pop()}`} style={{
      display: "flex", alignItems: "center", gap: 8, padding: "11px 12px",
      borderRadius: 10, border: "1px solid var(--pl-border)",
      background: "var(--pl-bg-2)", color: "var(--pl-text)", fontSize: 12.5, fontWeight: 700,
      textDecoration: "none", transition: "border-color 120ms",
    }}>
      <span style={{ color: "var(--pl-aqua)" }}>{icon}</span>{label}
    </Link>
  );
}

function QuickJob({ onCreated }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [wid, setWid] = useState("");
  const [prompt, setPrompt] = useState("PremierLab smoke test creative asset");
  const [jobType, setJobType] = useState("text_to_image");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/admin/workspaces").then((d) => {
      setWorkspaces(d.items || []);
      const sysWs = (d.items || []).find((w) => w.id === "ws-system-local") || (d.items || [])[0];
      if (sysWs) setWid(sysWs.id);
    }).catch(() => {});
  }, []);

  const fire = async () => {
    if (!wid) { setErr("Pick a workspace"); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const res = await api.post("/api/admin/quick-job", { workspace_id: wid, prompt, job_type: jobType });
      setResult(res);
      onCreated && onCreated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card title="Quick Job · smoke test" right={<Pill tone="lime">mock provider</Pill>}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Workspace">
          <select data-testid="qj-workspace" value={wid} onChange={(e) => setWid(e.target.value)} style={inputStyle}>
            {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name} · {w.id.slice(0, 10)}</option>)}
          </select>
        </Field>
        <Field label="Job type">
          <select data-testid="qj-job-type" value={jobType} onChange={(e) => setJobType(e.target.value)} style={inputStyle}>
            {["text_to_image", "image_to_video", "text_to_video", "voice"].map((t) =>
              <option key={t} value={t}>{t}</option>
            )}
          </select>
        </Field>
      </div>
      <div style={{ marginTop: 10 }}>
        <Field label="Prompt (prefix with “fail:” to test failure & refund)">
          <input data-testid="qj-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
            style={inputStyle} placeholder="A glossy aqua book cover…" />
        </Field>
      </div>
      <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
        <Btn testId="qj-fire" tone="primary" onClick={fire} disabled={busy}>
          <Zap size={13} /> {busy ? "Firing…" : "Fire job"}
        </Btn>
        {err && <span style={{ color: "#fda4af", fontSize: 12 }}>{err}</span>}
        {result && (
          <span style={{ fontSize: 11, color: "var(--pl-text-muted)" }}>
            <Pill tone="aqua">queued</Pill> · est&nbsp;{result.credits_estimated} cr · <code style={{ color: "var(--pl-aqua)" }}>{result.id.slice(0, 12)}</code>
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, color: "var(--pl-text-muted)", marginTop: 10, lineHeight: 1.55 }}>
        Quick Jobs fire the same worker pipeline used by Labs. Use them to verify credit reservation, debit, refund, and audit logging.
      </div>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800, marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}

function RecentJobs({ items }) {
  if (!items?.length) return <Empty msg="No recent jobs" />;
  return (
    <table style={tableStyles.table}>
      <tbody>
        {items.map((j) => (
          <tr key={j.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
            <td style={tableStyles.td}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{j.job_type}</div>
              <div style={{ fontSize: 10, color: "var(--pl-text-muted)", fontFamily: "JetBrains Mono, monospace" }}>{(j.id || "").slice(0, 10)}</div>
            </td>
            <td style={tableStyles.td}><StatusPill s={j.status} /></td>
            <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace", color: "var(--pl-aqua)" }}>
              {j.credits_charged ?? j.credits_estimated ?? 0} cr
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RecentTransactions({ items }) {
  if (!items?.length) return <Empty msg="No recent transactions" />;
  return (
    <table style={tableStyles.table}>
      <tbody>
        {items.map((t) => (
          <tr key={t.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
            <td style={tableStyles.td}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{t.type}</div>
              <div style={{ fontSize: 10, color: "var(--pl-text-muted)" }}>{(t.reason || "").slice(0, 40)}</div>
            </td>
            <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace", color: t.amount >= 0 ? "var(--pl-aqua)" : "#fcd34d" }}>
              {t.amount >= 0 ? `+${t.amount}` : t.amount}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RecentEvents({ items }) {
  if (!items?.length) return <Empty msg="No warnings or errors — all systems nominal." />;
  return (
    <table style={tableStyles.table}>
      <tbody>
        {items.map((e) => (
          <tr key={e.id || e.created_at + e.event_type} style={{ borderTop: "1px solid var(--pl-border)" }}>
            <td style={tableStyles.td}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{e.event_type}</div>
              <div style={{ fontSize: 10, color: "var(--pl-text-muted)" }}>{(e.message || "").slice(0, 56)}</div>
            </td>
            <td style={{ ...tableStyles.td, textAlign: "right" }}><StatusPill s={e.severity} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Empty({ msg }) {
  return <div style={{ padding: "18px 6px", color: "var(--pl-text-muted)", fontSize: 12, textAlign: "center" }}>{msg}</div>;
}

const inputStyle = {
  width: "100%", height: 34, padding: "0 10px", borderRadius: 9,
  background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)",
  color: "var(--pl-text)", fontSize: 12.5, outline: "none",
};
const linkStyle = { fontSize: 11, fontWeight: 700, color: "var(--pl-aqua)", textDecoration: "none", letterSpacing: "0.1em", textTransform: "uppercase" };

/* Backward-compat: keep these exports so other admin pages that previously imported from here keep working. */
export { Loading, Err };
export function Header({ title, sub, right }) {
  return <PageHead title={title} sub={sub} right={right} />;
}
